import { Clock, TrendingUp, Volume2 } from 'lucide-react';

import { UniqueBirdDetection } from '../types';
import { formatTime } from '../utils/dateFormatting';

interface SquareBirdCardProps {
  detection: UniqueBirdDetection;
  onClick?: () => void;
  isVocalizing?: boolean;
}

export function SquareBirdCard({ detection, onClick, isVocalizing = false }: SquareBirdCardProps) {
  if (!detection) {
    return null;
  }


  const getGradientColors = (species: string) => {
    if (!species || typeof species !== 'string') {
      return 'from-gray-400 to-gray-500';
    }
    
    const hash = species.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    const colors = [
      'from-purple-400 to-pink-500',
      'from-blue-400 to-purple-500',
      'from-green-400 to-blue-500',
      'from-yellow-400 to-orange-500',
      'from-pink-400 to-red-500',
      'from-indigo-400 to-purple-500',
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) {return 'text-green-400';}
    if (confidence >= 0.6) {return 'text-yellow-400';}
    return 'text-orange-400';
  };

  return (
    <div 
      className={`flex-shrink-0 w-32 h-32 rounded-xl border transition-all duration-300 cursor-pointer p-3 flex flex-col justify-between shadow-sm ${
        isVocalizing 
          ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-400 dark:border-emerald-500 ring-2 ring-emerald-400 dark:ring-emerald-500 animate-pulse' 
          : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700'
      }`}
      onClick={onClick}
    >
      {/* Header with bird initial */}
      <div className="flex items-center justify-between">
        <div className={`w-8 h-8 bg-gradient-to-br ${getGradientColors(detection.species)} rounded-lg flex items-center justify-center`}>
          <span className="text-white font-bold text-sm">
            {detection.common_name?.charAt(0) || '?'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {isVocalizing && <Volume2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400 animate-pulse" />}
          <TrendingUp className="w-3 h-3 text-blue-500 dark:text-blue-400" />
          <span className="text-xs text-neutral-700 dark:text-neutral-300 font-semibold">{detection.session_detections}</span>
        </div>
      </div>

      {/* Bird name */}
      <div className="flex-1 flex flex-col justify-center">
        <h3 className="text-neutral-900 dark:text-neutral-100 font-medium text-sm leading-tight line-clamp-2 text-center">
          {detection.common_name || 'Unknown Bird'}
        </h3>
      </div>

      {/* Footer with confidence and time */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className={`text-xs font-semibold ${getConfidenceColor(detection.max_confidence)}`}>
            {((detection.max_confidence || 0) * 100).toFixed(0)}%
          </span>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-neutral-500 dark:text-neutral-400" />
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {formatTime(detection.last_detected)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}